import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { eur } from "@/lib/format";

const KIND_LABEL = {
  deposit_hold: "Fianza preautorizada",
  deposit_charge: "Fianza cobrada",
  deposit_capture: "Captura de fianza",
  deposit_release: "Fianza liberada",
  deposit_refund: "Fianza devuelta",
  extras_charge: "Extras cobrados",
  rental_signal: "Señal cobrada",
  rental_signal_refund: "Señal devuelta",
  rental_charge: "Alquiler cobrado",
  post_rental_charge: "Cargo post-alquiler",
  hold_expired: "Retención de fianza caducada",
  dispute: "Disputa",
};

const KIND_COLOR = {
  deposit_charge: "text-marca",
  deposit_capture: "text-marca",
  extras_charge: "text-marca",
  rental_signal: "text-marca",
  rental_charge: "text-marca",
  post_rental_charge: "text-marca",
  rental_signal_refund: "text-[#B54434]",
  deposit_refund: "text-[#B54434]",
  hold_expired: "text-[#B54434]",
  dispute: "text-[#B54434]",
};

export default function Finance() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const params = vehicleId ? { vehicle_id: vehicleId } : {};

  useEffect(() => {
    api.get("/fleet/vehicles").then(({ data }) => setVehicles(data.vehicles || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const q = vehicleId ? { vehicle_id: vehicleId } : {};
      const [{ data: l }, { data: s }] = await Promise.all([
        api.get("/admin/ledger", { params: q }),
        api.get("/admin/finance/summary", { params: q }),
      ]);
      setRows(l.rows || []);
      setSummary(s);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, [vehicleId]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const resp = await api.get("/admin/ledger/export.csv", { responseType: "blob", params });
      const slug = vehicles.find((v) => v.id === vehicleId)?.slug;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = slug ? `ledger-${slug}.csv` : "ledger.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] pb-12">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca" data-testid="finance-back">
            <ArrowLeft className="h-4 w-4 mr-2" /> Panel
          </Link>
          <div className="flex items-center gap-2">
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}
              className="h-9 rounded-md border border-[#DCD8D0] bg-white px-2 text-sm" data-testid="finance-vehicle">
              <option value="">Todos los vehículos</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <Button onClick={exportCsv} variant="outline" className="border-marca text-marca" data-testid="finance-export-btn">
              <Download className="h-4 w-4 mr-2" /> Exportar CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="container-x py-8">
        <span className="text-overline">Conciliación</span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A] mt-1 mb-6">
          Finanzas
        </h1>

        {summary && (
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
              <div className="text-overline mb-1">Total cobrado</div>
              <div className="font-display text-2xl font-bold text-marca">
                {eur(summary.total_charged)}
              </div>
            </div>
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
              <div className="text-overline mb-1">Total devuelto</div>
              <div className="font-display text-2xl font-bold text-acento">
                {eur(summary.total_refunded)}
              </div>
            </div>
            <div className="bg-white border border-[#DCD8D0] rounded-xl p-5">
              <div className="text-overline mb-1">Neto</div>
              <div className="font-display text-2xl font-bold text-[#1A1A1A]">
                {eur(summary.net)}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#DCD8D0] rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="border-b border-[#DCD8D0] text-left text-xs uppercase tracking-wider text-[#8A8A8A]">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Reserva</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Movimiento</th>
                <th className="px-4 py-3 text-right">Importe</th>
                <th className="px-4 py-3">Ref. Stripe</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[#F0EDE6] hover:bg-[#F7F5F0]/60">
                  <td className="px-4 py-3 text-[#5C5C5C] whitespace-nowrap">
                    {new Date(r.created_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-marca bg-[#EAE7E0] px-2 py-0.5 rounded">
                      {r.reservation_code}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#1A1A1A]">{r.client_name}</td>
                  <td className="px-4 py-3">
                    <span className={KIND_COLOR[r.kind] || "text-[#5C5C5C]"}>
                      {KIND_LABEL[r.kind] || r.kind}
                    </span>
                    {r.note && <div className="text-xs text-[#8A8A8A]">{r.note}</div>}
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${KIND_COLOR[r.kind] || ""}`}>
                    {eur(r.amount)}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-[#8A8A8A] max-w-[180px] truncate">
                    {r.stripe_ref || "—"}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#8A8A8A]">Sin movimientos todavía.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
